import React, { useEffect, useState } from 'react';
import { surveyAPI } from '@/api/api';
import { Star, Loader2, CheckCircle2, ClipboardList, ChevronRight, Lock } from 'lucide-react';

const ETI_LOGO = 'https://customer-assets.emergentagent.com/job_4e0bdddc-c844-4374-a91a-dfbddecb14b1/artifacts/4ane8ulw_eti%20.png';

const RatingInput = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-2" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        data-testid={`${testid}-star-${n}`}
        className="transition-transform hover:scale-110 focus:outline-none"
        aria-label={`${n} star`}
      >
        <Star
          className={`h-8 w-8 ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
        />
      </button>
    ))}
    {value > 0 && <span className="ml-2 text-sm font-medium text-slate-500">{value}/5</span>}
  </div>
);

const StudentSurveyPage = () => {
  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState(null);
  const [branches, setBranches] = useState([]);
  const [meta, setMeta] = useState({ branch_id: '', student_name: '', enrollment_number: '', program: '' });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [pasteFlash, setPasteFlash] = useState(false);

  const blockPaste = (e) => {
    e.preventDefault();
    setPasteFlash(true);
    setTimeout(() => setPasteFlash(false), 2500);
  };
  const noPasteProps = {
    onPaste: blockPaste,
    onDrop: blockPaste,
    onContextMenu: (e) => e.preventDefault(),
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await surveyAPI.getPublic();
        setSurvey(res.data.survey);
        setBranches(res.data.branches || []);
      } catch (e) {
        setError('Could not load the survey. Please try again later.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setAnswer = (qid, val) => setAnswers((p) => ({ ...p, [qid]: val }));

  const MIN_WORDS = 5;
  const wordCount = (v) => (String(v || '').trim() ? String(v).trim().split(/\s+/).length : 0);

  const handleSubmit = async () => {
    setError('');
    if (!meta.branch_id) {
      setError('Please select your branch.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    for (const q of survey.questions) {
      if (q.required) {
        const wc = wordCount(answers[q.id]);
        if (wc === 0) {
          setError(`Please answer: ${q.label}`);
          return;
        }
        if (wc < MIN_WORDS) {
          setError(`Please write at least ${MIN_WORDS} words for: ${q.label}`);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      await surveyAPI.submit({ ...meta, answers });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-blue-50 p-4">
        <div className="max-w-md w-full rounded-2xl bg-white p-8 text-center shadow-xl ring-1 ring-slate-200" data-testid="survey-thankyou">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
            <CheckCircle2 className="h-9 w-9 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Thank you!</h1>
          <p className="mt-2 text-slate-600">
            Your feedback helps ETI Educom improve every student's learning experience. We truly appreciate your time.
          </p>
          <img src={ETI_LOGO} alt="ETI Educom" className="mx-auto mt-6 h-10 w-auto opacity-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50 py-8 px-4">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <img src={ETI_LOGO} alt="ETI Educom" className="h-10 w-auto" />
            <div className="flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              <ClipboardList className="h-3.5 w-3.5" /> Student Experience Survey
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="survey-title">{survey.title}</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{survey.subtitle}</p>
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
            <Lock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span>Please write every answer in your own words. Copy-paste is disabled so we hear your genuine thoughts.</span>
          </div>
        </div>

        {pasteFlash && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="survey-paste-blocked">
            Pasting isn't allowed here — please type your answer in your own words.
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="survey-error">
            {error}
          </div>
        )}

        {/* Identity */}
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200 mb-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Your Branch <span className="text-red-500">*</span></label>
            <select
              data-testid="survey-branch-select"
              value={meta.branch_id}
              onChange={(e) => setMeta({ ...meta, branch_id: e.target.value })}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select your branch</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}{b.city ? ` — ${b.city}` : ''}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Your Name <span className="text-slate-400 font-normal">(optional)</span></label>
              <input
                data-testid="survey-name-input"
                value={meta.student_name}
                onChange={(e) => setMeta({ ...meta, student_name: e.target.value })}
                {...noPasteProps}
                placeholder="Leave blank to stay anonymous"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Course / Program <span className="text-slate-400 font-normal">(optional)</span></label>
              <input
                data-testid="survey-program-input"
                value={meta.program}
                onChange={(e) => setMeta({ ...meta, program: e.target.value })}
                {...noPasteProps}
                placeholder="e.g. Full Stack, Digital Marketing"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-4">
          {survey.questions.map((q, idx) => (
            <div key={q.id} className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200" data-testid={`survey-q-${q.id}`}>
              <div className="flex items-start gap-2 mb-4">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">{idx + 1}</span>
                <label className="text-[15px] font-medium text-slate-800">
                  {q.label}{q.required && <span className="text-red-500"> *</span>}
                </label>
              </div>

              {q.type === 'rating' && (
                <RatingInput value={answers[q.id] || 0} onChange={(v) => setAnswer(q.id, v)} testid={`rating-${q.id}`} />
              )}

              {q.type === 'choice' && (
                <div className="flex flex-wrap gap-2">
                  {q.options.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      data-testid={`choice-${q.id}-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => setAnswer(q.id, opt)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                        answers[q.id] === opt
                          ? 'border-blue-600 bg-blue-600 text-white'
                          : 'border-slate-300 bg-white text-slate-700 hover:border-blue-400'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}

              {q.type === 'text' && (
                <div>
                  <textarea
                    data-testid={`text-${q.id}`}
                    value={answers[q.id] || ''}
                    onChange={(e) => setAnswer(q.id, e.target.value)}
                    {...noPasteProps}
                    rows={4}
                    placeholder="Type your answer in your own words..."
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  {q.required && (
                    <div
                      className={`mt-1.5 text-xs ${wordCount(answers[q.id]) >= MIN_WORDS ? 'text-emerald-600' : 'text-slate-400'}`}
                      data-testid={`wordcount-${q.id}`}
                    >
                      {wordCount(answers[q.id]) >= MIN_WORDS
                        ? `✓ ${wordCount(answers[q.id])} words`
                        : `${wordCount(answers[q.id])}/${MIN_WORDS} words minimum — please write a little more`}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Submit */}
        <button
          data-testid="survey-submit-button"
          onClick={handleSubmit}
          disabled={submitting}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-base font-semibold text-white shadow-lg transition-colors hover:bg-blue-700 disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Submit Feedback <ChevronRight className="h-5 w-5" /></>}
        </button>
        <p className="mt-4 text-center text-xs text-slate-400">Your responses are confidential and used only to improve ETI Educom.</p>
      </div>
    </div>
  );
};

export default StudentSurveyPage;
